import { Snackbar } from "~/common/mui";
import { useEffect, useState } from "react";
import type { DetailedOrderData } from "~/domains/orders/types/modelsV2";
import { CreateCaseSource, type CaseDataObject } from "~/features/orders/orderDetails/components/ServiceCases/types";
import { useSendOrderFeedback } from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/useSendOrderFeedback";
import { SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { useDispatch } from "react-redux";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { fetchOrderDetails } from "~/features/orders/orderList/api/fetchOrderDetails/fetchOrderDetails";
import { fetchServiceCases } from "~/features/orders/orderDetails/components/ServiceCases/api";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import ServiceCaseListDialog from "./ServiceCaseListDialog";

export interface OrderDetailsDisplayState {
  loading: boolean;
  error: boolean;
  orderData?: DetailedOrderData;
  serviceCases?: Record<string, CaseDataObject[]>;
}

const ServiceCreationFlow = ({
  openServiceCaseListDialog,
  onClose,
  soNumber,
  poNumber,
  orderLineNo,
  subLineNo,
}: {
  openServiceCaseListDialog: boolean;
  onClose: () => void;
  soNumber: string;
  poNumber: string;
  orderLineNo: string;
  subLineNo: string;
}) => {
  const dispatch = useDispatch();

  const [state, setState] = useState<OrderDetailsDisplayState>({
    loading: true,
    error: false,
  });

  const [snackbarData, setSnackbarData] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  useEffect(() => {
    const loadData = async () => {
      if (!soNumber) {
        setState({
          loading: false,
          error: false,
        });
        return;
      }
      setState({
        loading: true,
        error: false,
        orderData: undefined,
        serviceCases: undefined,
      });
      try {
        let orderData;
        let serviceCases = {};
        if (userHasPermission(Permission.CAN_SEE_SERVICE_CASES)) {
          [orderData, serviceCases] = await Promise.all([fetchOrderDetails(soNumber!), fetchServiceCases(soNumber)]);
        } else {
          orderData = await fetchOrderDetails(soNumber);
        }
        dispatch(addBaseProductDataAction(orderData.products.map((entry) => entry.product)));
        setState({
          orderData,
          serviceCases,
          loading: false,
          error: false,
        });
      } catch {
        setState({
          loading: false,
          error: true,
        });
      }
    };
    loadData();
  }, [soNumber, dispatch]);

  const { orderData } = state;

  const data = orderData?.products.find((item) => item.lineNo === orderLineNo);

  const handleCaseCreated = () => {
    onClose();
  };

  const [createServiceCasePopups, open, userHasCreatedCase] = useSendOrderFeedback(
    data,
    orderData,
    Number(orderLineNo),
    soNumber,
    poNumber,
    setSnackbarData,
    subLineNo,
    CreateCaseSource.LIST_PAGE,
    handleCaseCreated,
  );

  const serviceCasesArray = state?.serviceCases?.[orderLineNo] ?? [];

  return (
    <>
      {openServiceCaseListDialog && (
        <ServiceCaseListDialog
          onClose={onClose}
          loading={state.loading}
          serviceCases={serviceCasesArray?.filter((item) => item.completeData)}
          onAddCaseClick={open}
          setSnackbar={setSnackbarData}
          userHasCreatedCase={userHasCreatedCase}
        />
      )}
      <Snackbar
        onClose={() => setSnackbarData((prev) => ({ ...prev, open: false }))}
        open={snackbarData.open}
        autoHideDuration={7000}
        transitionDuration={{ exit: 500, appear: 500, enter: 500 }}
        message={snackbarData.message}
        action={<SnackbarCloseButton onClick={() => setSnackbarData((prev) => ({ ...prev, open: false }))} />}
      />
      {createServiceCasePopups}
    </>
  );
};

export default ServiceCreationFlow;
